import { IApiError } from '../../../models/api';
import { IShippingCompany } from '../../../models/shippingCompany';
import { ApiService } from '../../../services/api';
import { CONSTANTS } from '../../../utils/constants';
import { getErrorMessages } from '../../../utils/formatters';
import { generateAsyncActionNames } from '../../../utils/helpers';
import { AppDispatch, RootState } from '../../type';
import { TableActions } from '../table/table.actions';
import { GetShippingCompaniesPayload } from './type';

const { get, post, put } = ApiService;

enum ShippingCompaniesActionsEnum {
  GET_SHIPPING_COMPANIES = 'GET_SHIPPING_COMPANIES',
  ADD_SHIPPING_COMPANY = 'ADD_SHIPPING_COMPANY',
  UPDATE_SHIPPING_COMPANY = 'UPDATE_SHIPPING_COMPANY',
  RELINK_SHIPPING_COMPANY_CLIENT = 'RELINK_SHIPPING_COMPANY_CLIENT',
}

export const ShippingCompaniesActionsNames = {
  [ShippingCompaniesActionsEnum.GET_SHIPPING_COMPANIES]:
    generateAsyncActionNames(
      ShippingCompaniesActionsEnum.GET_SHIPPING_COMPANIES
    ),
  [ShippingCompaniesActionsEnum.ADD_SHIPPING_COMPANY]: generateAsyncActionNames(
    ShippingCompaniesActionsEnum.ADD_SHIPPING_COMPANY
  ),
  [ShippingCompaniesActionsEnum.UPDATE_SHIPPING_COMPANY]:
    generateAsyncActionNames(
      ShippingCompaniesActionsEnum.UPDATE_SHIPPING_COMPANY
    ),
  [ShippingCompaniesActionsEnum.RELINK_SHIPPING_COMPANY_CLIENT]:
    generateAsyncActionNames(
      ShippingCompaniesActionsEnum.RELINK_SHIPPING_COMPANY_CLIENT
    ),
};

const Actions = {
  [ShippingCompaniesActionsEnum.GET_SHIPPING_COMPANIES]: {
    START: () => ({
      type: ShippingCompaniesActionsNames.GET_SHIPPING_COMPANIES.START,
    }),
    FULFILLED: (data: GetShippingCompaniesPayload) => ({
      type: ShippingCompaniesActionsNames.GET_SHIPPING_COMPANIES.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ShippingCompaniesActionsNames.GET_SHIPPING_COMPANIES.REJECTED,
      payload: error,
    }),
  },
  [ShippingCompaniesActionsEnum.ADD_SHIPPING_COMPANY]: {
    START: () => ({
      type: ShippingCompaniesActionsNames.ADD_SHIPPING_COMPANY.START,
    }),
    FULFILLED: () => ({
      type: ShippingCompaniesActionsNames.ADD_SHIPPING_COMPANY.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ShippingCompaniesActionsNames.ADD_SHIPPING_COMPANY.REJECTED,
      payload: error,
    }),
  },
  [ShippingCompaniesActionsEnum.UPDATE_SHIPPING_COMPANY]: {
    START: () => ({
      type: ShippingCompaniesActionsNames.UPDATE_SHIPPING_COMPANY.START,
    }),
    FULFILLED: () => ({
      type: ShippingCompaniesActionsNames.UPDATE_SHIPPING_COMPANY.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ShippingCompaniesActionsNames.UPDATE_SHIPPING_COMPANY.REJECTED,
      payload: error,
    }),
  },
  [ShippingCompaniesActionsEnum.RELINK_SHIPPING_COMPANY_CLIENT]: {
    START: () => ({
      type: ShippingCompaniesActionsNames.RELINK_SHIPPING_COMPANY_CLIENT.START,
    }),
    FULFILLED: () => ({
      type: ShippingCompaniesActionsNames.RELINK_SHIPPING_COMPANY_CLIENT
        .FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: ShippingCompaniesActionsNames.RELINK_SHIPPING_COMPANY_CLIENT
        .REJECTED,
      payload: error,
    }),
  },
};

const getShippingCompanies =
  (
    sort: { Field: string; IsDescending: boolean }[] = [],
    updateTable = true,
    pageNumber?: number
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(
      Actions[ShippingCompaniesActionsEnum.GET_SHIPPING_COMPANIES].START()
    );
    try {
      const table = getState().table;
      const requestData = {
        headers: {
          'X-Pagination': JSON.stringify({
            PageNumber: pageNumber ?? table.currentPage,
            PageSize: table.pageSize,
          }),
          ...(sort.length
            ? { 'X-Sorting': JSON.stringify({ Sortings: sort }) }
            : {}),
        },
      };
      const { data } = await get(
        'ShippingCompany/GetShippingCompanies',
        requestData
      );
      if (!data.isSucceeded) throw Error(JSON.stringify(data.errors));
      dispatch(
        Actions[ShippingCompaniesActionsEnum.GET_SHIPPING_COMPANIES].FULFILLED(
          data?.content?.shippingCompanies ?? []
        )
      );
      if (updateTable) {
        dispatch(
          TableActions.setTableData({
            totalRows: data?.content?.totalCount ?? 0,
            currentPage: pageNumber ?? CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
          })
        );
      }
    } catch (error) {
      dispatch(
        Actions[ShippingCompaniesActionsEnum.GET_SHIPPING_COMPANIES].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const addShippingCompany =
  (body: Omit<IShippingCompany, 'id' | 'applicationId'>) =>
  async (dispatch: AppDispatch) => {
    dispatch(
      Actions[ShippingCompaniesActionsEnum.ADD_SHIPPING_COMPANY].START()
    );
    try {
      const { data }: { data: ApiResponse } = await post(
        'ShippingCompany/AddShippingCompany',
        body
      );
      if (!data.isSucceeded) throw Error(JSON.stringify(data.errors));
      dispatch(
        Actions[ShippingCompaniesActionsEnum.ADD_SHIPPING_COMPANY].FULFILLED()
      );
      return data;
    } catch (error) {
      dispatch(
        Actions[ShippingCompaniesActionsEnum.ADD_SHIPPING_COMPANY].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const updateShippingCompany =
  (id: number, body: Omit<IShippingCompany, 'id' | 'applicationId'>) =>
  async (dispatch: AppDispatch) => {
    dispatch(
      Actions[ShippingCompaniesActionsEnum.UPDATE_SHIPPING_COMPANY].START()
    );
    try {
      const { data }: { data: ApiResponse } = await put(
        `/ShippingCompany/UpdateShippingCompany/${id}`,
        body
      );
      if (!data.isSucceeded) throw Error(JSON.stringify(data.errors));
      dispatch(
        Actions[
          ShippingCompaniesActionsEnum.UPDATE_SHIPPING_COMPANY
        ].FULFILLED()
      );
      return data;
    } catch (error) {
      dispatch(
        Actions[ShippingCompaniesActionsEnum.UPDATE_SHIPPING_COMPANY].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const relinkShippingCompanyClient =
  (id: number) => async (dispatch: AppDispatch) => {
    dispatch(
      Actions[
        ShippingCompaniesActionsEnum.RELINK_SHIPPING_COMPANY_CLIENT
      ].START()
    );
    try {
      const { data }: { data: ApiResponse } = await put(
        `/ShippingCompany/RelinkShippingCompanyClient/${id}`,
        {}
      );
      if (!data.isSucceeded) throw Error(JSON.stringify(data.errors));
      dispatch(
        Actions[
          ShippingCompaniesActionsEnum.RELINK_SHIPPING_COMPANY_CLIENT
        ].FULFILLED()
      );
      return data;
    } catch (error) {
      dispatch(
        Actions[
          ShippingCompaniesActionsEnum.RELINK_SHIPPING_COMPANY_CLIENT
        ].REJECTED(getErrorMessages(error))
      );
    }
  };

export const ShippingCompaniesActions = {
  getShippingCompanies,
  addShippingCompany,
  updateShippingCompany,
  relinkShippingCompanyClient,
};
